import { z } from 'zod';
import { LatLng, LocalDate, Slug, Timestamp, Ulid } from './common.js';
import { AdapterType, Category, Province, Trust } from './enums.js';
/** Configurazione di una sorgente (sse-ingestion/sources/<id>.yaml). */
export const Source = z
    .object({
    id: Slug,
    name: z.string(),
    adapter: AdapterType,
    url: z.url().optional(),
    /** espressione EventBridge Scheduler, valutata in Europe/Rome */
    schedule: z.string().optional(),
    trust: Trust,
    enabled: z.boolean().default(true),
    autoPublish: z.boolean().default(false),
    defaults: z
        .object({ comune: z.string().optional(), category: Category.optional() })
        .default({}),
    fetch: z
        .object({
        rateLimitSeconds: z.number().min(1).default(3),
        useBrowser: z.boolean().default(false),
    })
        .default({ rateLimitSeconds: 3, useBrowser: false }),
    config: z.record(z.string(), z.unknown()).default({}),
})
    .meta({ id: 'Source' });
export const SourceHealth = z
    .object({
    lastRunAt: Timestamp.optional(),
    lastSuccessAt: Timestamp.optional(),
    lastError: z.string().optional(),
    consecutiveFailures: z.int().nonnegative().default(0),
    runsWithoutNews: z.int().nonnegative().default(0),
    itemsLastRun: z.int().nonnegative().default(0),
})
    .meta({ id: 'SourceHealth' });
/** Traccia di un elemento letto da una sorgente (change detection). */
export const RawItem = z
    .object({
    sourceId: z.string(),
    externalKey: z.string(),
    contentHash: z.string(),
    lastSeen: Timestamp,
    rawS3Key: z.string().optional(),
    candidateId: z.string().optional(),
    eventId: z.string().optional(),
})
    .meta({ id: 'RawItem' });
export const Frazione = z
    .object({
    name: z.string(),
    slug: Slug,
    lat: z.number().optional(),
    lng: z.number().optional(),
})
    .meta({ id: 'Frazione' });
/** Comune del gazetteer (codice ISTAT a 6 cifre). */
export const Comune = z
    .object({
    istat: z.string().regex(/^\d{6}$/),
    name: z.string(),
    slug: Slug,
    province: Province,
    centroid: LatLng,
    frazioni: z.array(Frazione).default([]),
    aliases: z.array(z.string()).default([]),
})
    .meta({ id: 'Comune' });
export const Gazetteer = z
    .object({
    version: LocalDate,
    comuni: z.array(Comune),
})
    .meta({ id: 'Gazetteer' });
export const RunStatus = z.enum(['running', 'completed', 'failed']).meta({ id: 'RunStatus' });
/**
 * Contatori di un'esecuzione, aggiornati in modo atomico dalle Lambda (ADD su DynamoDB).
 * L'esecuzione è finita quando ogni fase ha elaborato quello che la precedente le ha passato:
 * `fetched >= items`, `extracted >= changed`, `resolved >= candidates`.
 */
export const RunCounters = z
    .object({
    /** elementi trovati da discover (pagine, numeri della rivista, ...) */
    items: z.int().nonnegative().default(0),
    /** elementi scaricati (o falliti): fase fetch conclusa */
    fetched: z.int().nonnegative().default(0),
    unchanged: z.int().nonnegative().default(0),
    changed: z.int().nonnegative().default(0),
    failed: z.int().nonnegative().default(0),
    /** elementi cambiati già estratti */
    extracted: z.int().nonnegative().default(0),
    candidates: z.int().nonnegative().default(0),
    resolved: z.int().nonnegative().default(0),
    new: z.int().nonnegative().default(0),
    duplicate: z.int().nonnegative().default(0),
    update: z.int().nonnegative().default(0),
    uncertain: z.int().nonnegative().default(0),
    invalid: z.int().nonnegative().default(0),
    published: z.int().nonnegative().default(0),
})
    .meta({ id: 'RunCounters' });
/** Esecuzione di una sorgente (pianificata o avviata dal pannello). docs/03-modello-dati.md §2. */
export const Run = z
    .object({
    id: Ulid,
    sourceId: z.string(),
    trigger: z.enum(['schedule', 'manual']),
    /** utente che l'ha avviata dal pannello */
    by: z.string().optional(),
    status: RunStatus,
    startedAt: Timestamp,
    finishedAt: Timestamp.optional(),
    counters: RunCounters.default(RunCounters.parse({})),
    /** qualità dell'estrazione per elemento (es. copertura del PDF), chiave = externalKey */
    quality: z.record(z.string(), z.record(z.string(), z.unknown())).default({}),
    error: z.string().optional(),
    schemaVersion: z.int().positive(),
})
    .meta({ id: 'Run' });
//# sourceMappingURL=source.js.map